import { createContext, useContext, useState, useCallback, useRef } from "react";
import axios from "../utils/axios";
import { toast } from "sonner";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  // ===================== ADMIN AUTH =====================
  const fetchAdmin = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.get("/admin/profile");
      setAdmin(res.data.admin);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const registerAdmin = async (name, email, password, role = "admin") => {
    try {
      await axios.post("/admin/register", { name, email, password, role });
      toast.success("Admin account created!");
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to register admin.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const res = await axios.post("/admin/login", { email, password, rememberMe });
    
    // Check if OTP is required
    const requiresOTP = res.data?.requiresOTP === true;
    
    // If OTP is required, return early without fetching admin profile
    // Store rememberMe in the return value so it can be passed to verifyOTP
    if (requiresOTP) {
      return { 
        requiresOTP: true, 
        message: res.data?.message || "Password verified. Please check your email for OTP verification.",
        rememberMe: rememberMe // Pass rememberMe through for OTP verification
      };
    }
    
    // If no OTP required, login is complete - fetch admin profile
    await fetchAdmin();
    return { requiresOTP: false };
  };

  const verifyOTP = async (email, otp, rememberMe = false) => {
    try {
      // Note: rememberMe is stored in Redis during login, so we don't need to pass it again
      // But we'll keep the parameter for consistency and future use
      const res = await axios.post("/admin/verify-otp", { email, otp });
      await fetchAdmin();
      return res.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "OTP verification failed. Please check your code and try again.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const resendOTP = async (email) => {
    try {
      const res = await axios.post("/admin/resend-otp", { email });
      toast.success(res.data.message || "OTP has been resent to your email.");
      return res.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to resend OTP.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    await axios.post("/admin/logout");
    setAdmin(null);
    toast.success("Logged out successfully!");
  };

  const getAllUsers = async (page = 1, limit = 20, status = "active", search = "") => {
    try {
      const res = await axios.get("/users", {
        params: { page, limit, status, search },
      });
      return res.data; // return { users, total, page, limit, totalPages, hasNextPage, hasPrevPage }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users.");
      return {
        users: [],
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      };
    }
  };

  // ===================== CLOUDINARY FILE METHODS =====================
  const uploadFiles = async (formData) => {
    try {
      const res = await axios.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Files uploaded successfully!");
      return res.data.files;
    } catch (error) {
      const message = error?.response?.data?.message || "File upload failed.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const uploadFileFromUrl = async (url) => {
    try {
      const res = await axios.post("/files/upload-url", { url });
      toast.success("Image uploaded from URL successfully!");
      return res.data.files;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to upload from URL.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const uploadAdminAvatar = async (file) => {
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const res = await axios.put("/admin/profile/avatar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Update admin state with new avatar URL
      if (res.data?.admin) {
        setAdmin(res.data.admin);
      }

      toast.success(res.data?.message || "Avatar uploaded successfully!");
      return res.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to upload avatar.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getFiles = async (page = 1, limit = 100) => {
    try {
      const res = await axios.get("/files/list", {
        params: { page, limit }
      });
      return {
        files: res.data.files || [],
        pagination: res.data.pagination || {
          page: 1,
          limit,
          total: res.data.files?.length || 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        }
      };
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch files.";
      toast.error(message);
      return {
        files: [],
        pagination: {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        }
      };
    }
  };

  const deleteFiles = async (ids) => {
    try {
      await axios.delete("/files/delete", { data: { ids } });
      toast.success("Files deleted successfully!");
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to delete files.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== COUPON METHODS =====================
  const createCoupon = async (couponData) => {
    try {
      await axios.post("/coupons", couponData);
      toast.success("Coupon created successfully!");
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to create coupon.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const updateCoupon = async (id, couponData) => {
    try {
      await axios.put(`/coupons/${id}`, couponData);
      toast.success("Coupon updated successfully!");
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to update coupon.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await axios.delete(`/coupons/${id}`);
      toast.success("Coupon deleted successfully!");
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to delete coupon.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const listCoupons = async (filters = {}) => {
    try {
      const res = await axios.get("/coupons", { params: filters });
      // Backend returns { success: true, data: [...], pagination: {...} }
      if (res.data?.success && Array.isArray(res.data.data)) {
        return res.data.data; // Return array directly for backward compatibility
      }
      // Fallback for old format or direct array
      if (Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to fetch coupons.";
      toast.error(message);
      return [];
    }
  };

  const validateCoupon = async (data) => {
    try {
      const res = await axios.post("/coupons/validate", data);
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Coupon validation failed.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const applyCoupon = async (data) => {
    try {
      await axios.post("/coupons/apply", data);
      toast.success("Coupon applied successfully!");
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to apply coupon.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getCouponInfo = async () => {
    try {
      const res = await axios.get("/coupons/info");
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to get coupon info.";
      toast.error(message);
      return null;
    }
  };

  // ===================== DASHBOARD STATS =====================
  const getMonthlyStats = async (startDate, endDate) => {
    try {
      const res = await axios.get("/reports/monthly-stats", {
        params: { startDate, endDate },
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
      toast.error("Failed to fetch monthly stats.");
      return [];
    }
  };

  const getRecentOrders = async () => {
    try {
      const res = await axios.get("/reports/recent-orders");
      return res.data;
    } catch (error) {
      console.error("Error fetching recent orders:", error);
      toast.error("Failed to fetch recent orders.");
      return [];
    }
  };

  // ===================== ADMIN TICKET METHODS =====================
  const getAllTickets = async (page = 1, limit = 10, filters = {}) => {
    try {
      const res = await axios.get("/admin/tickets", {
        params: {
          page,
          limit,
          ...filters,
        },
      });
      return res.data; // Returns { tickets, pagination, filters }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to fetch tickets.");
      return {
        tickets: [],
        pagination: { page: 1, limit: 10, total: 0, totalPages: 0, hasMore: false },
        filters: {},
      };
    }
  };

  const getTicketWithReplies = async (ticketId) => {
    try {
      const res = await axios.get(`/admin/tickets/${ticketId}`);
      return res.data; // Returns { ticket, messages }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      const message = error?.response?.data?.message || "Failed to fetch ticket details.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const replyToTicket = async (ticketId, message) => {
    try {
      const res = await axios.post(`/admin/tickets/${ticketId}/reply`, { message });
      toast.success("Reply sent successfully.");
      return res.data;
    } catch (error) {
      console.error("Error sending reply:", error);
      const message = error?.response?.data?.message || "Failed to send reply.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const changeTicketStatus = async (ticketId, status) => {
    try {
      const res = await axios.put(`/admin/tickets/${ticketId}`, { status });
      toast.success(`Ticket status updated to ${status}`);
      return res.data;
    } catch (error) {
      console.error("Error changing ticket status:", error);
      const message = error?.response?.data?.message || "Failed to change ticket status.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== SYSTEM SETTINGS METHODS =====================
  const getAllSystemSettings = async () => {
    try {
      const res = await axios.get("/admin/system-settings");
      return res.data?.data || {};
    } catch (error) {
      console.error("Error fetching system settings:", error);
      toast.error("Failed to fetch system settings.");
      return {};
    }
  };

  const getSystemSetting = async (key) => {
    try {
      const res = await axios.get(`/admin/system-settings/${key}`);
      return res.data?.data?.value;
    } catch (error) {
      console.error("Error fetching system setting:", error);
      toast.error("Failed to fetch system setting.");
      return null;
    }
  };

  const updateSystemSetting = async (key, enabled) => {
    try {
      const res = await axios.put(`/admin/system-settings/${key}`, { enabled });
      toast.success(res.data?.message || "Setting updated successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error updating system setting:", error);
      const message = error?.response?.data?.error?.message || "Failed to update system setting.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getPaymentGatewayStatus = async (gateway) => {
    try {
      const res = await axios.get(`/admin/system-settings/payment-gateway/${gateway}/status`);
      return res.data?.data?.enabled || false;
    } catch (error) {
      console.error("Error fetching payment gateway status:", error);
      return false;
    }
  };

  const enablePaymentGateway = async (gateway) => {
    try {
      const res = await axios.post(`/admin/system-settings/payment-gateway/${gateway}/enable`);
      toast.success(res.data?.message || "Payment gateway enabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error enabling payment gateway:", error);
      const message = error?.response?.data?.error?.message || "Failed to enable payment gateway.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const disablePaymentGateway = async (gateway) => {
    try {
      const res = await axios.post(`/admin/system-settings/payment-gateway/${gateway}/disable`);
      toast.success(res.data?.message || "Payment gateway disabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error disabling payment gateway:", error);
      const message = error?.response?.data?.error?.message || "Failed to disable payment gateway.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getTaxRate = async () => {
    try {
      const res = await axios.get("/admin/system-settings/tax-rate");
      return res.data?.data?.value || 0;
    } catch (error) {
      console.error("Error fetching tax rate:", error);
      return 0;
    }
  };

  const updateTaxRate = async (taxRate) => {
    try {
      const res = await axios.put("/admin/system-settings/tax-rate", { taxRate });
      toast.success(res.data?.message || "Tax rate updated successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error updating tax rate:", error);
      const message = error?.response?.data?.error?.message || "Failed to update tax rate.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getSiteInfo = async () => {
    try {
      const res = await axios.get("/admin/system-settings/site-info");
      return res.data?.data || {
        siteName: "GlowSkin",
        siteDescription: "Premium skincare products for radiant skin.",
        contactEmail: "support@glowskin.com",
      };
    } catch (error) {
      console.error("Error fetching site information:", error);
      return {
        siteName: "GlowSkin",
        siteDescription: "Premium skincare products for radiant skin.",
        contactEmail: "support@glowskin.com",
      };
    }
  };

  const updateSiteInfo = async (siteInfo) => {
    try {
      const res = await axios.put("/admin/system-settings/site-info", siteInfo);
      toast.success(res.data?.message || "Site information updated successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error updating site information:", error);
      const message = error?.response?.data?.error?.message || "Failed to update site information.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const enableDbBackup = async () => {
    try {
      const res = await axios.post("/admin/system-settings/db-backup/enable");
      toast.success(res.data?.message || "Database backup enabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error enabling database backup:", error);
      const message = error?.response?.data?.error?.message || "Failed to enable database backup.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const disableDbBackup = async () => {
    try {
      const res = await axios.post("/admin/system-settings/db-backup/disable");
      toast.success(res.data?.message || "Database backup disabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error disabling database backup:", error);
      const message = error?.response?.data?.error?.message || "Failed to disable database backup.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const enableSentry = async () => {
    try {
      const res = await axios.post("/admin/system-settings/sentry/enable");
      toast.success(res.data?.message || "Sentry logging enabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error enabling Sentry:", error);
      const message = error?.response?.data?.error?.message || "Failed to enable Sentry logging.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const disableSentry = async () => {
    try {
      const res = await axios.post("/admin/system-settings/sentry/disable");
      toast.success(res.data?.message || "Sentry logging disabled successfully.");
      return res.data?.data;
    } catch (error) {
      console.error("Error disabling Sentry:", error);
      const message = error?.response?.data?.error?.message || "Failed to disable Sentry logging.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== EMAIL BROADCAST METHODS =====================
  const sendBulkEmail = async (subject, content, userIds = null, attachments = []) => {
    try {
      const res = await axios.post("/email/broadcast", {
        subject,
        content,
        userIds: userIds || undefined, // Send undefined if null to send to all
        attachments,
      });
      toast.success(res.data?.message || `Queued ${res.data?.queued || 0} emails for sending.`);
      return res.data;
    } catch (error) {
      console.error("Error sending bulk email:", error);
      const message = error?.response?.data?.message || "Failed to send bulk email.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const sendPreviewEmail = async (subject, content, attachments = [], previewEmail = null) => {
    try {
      const requestBody = {
        subject,
        content,
        attachments,
      };
      
      // Add previewEmail if provided
      if (previewEmail && previewEmail.trim()) {
        requestBody.previewEmail = previewEmail.trim();
      }
      
      const res = await axios.post("/email/preview", requestBody);
      toast.success(res.data?.message || "Preview email sent successfully.");
      return res.data;
    } catch (error) {
      console.error("Error sending preview email:", error);
      const message = error?.response?.data?.message || "Failed to send preview email.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getEmailLogs = async (page = 1, limit = 20, filters = {}) => {
    try {
      const res = await axios.get("/email/logs", {
        params: {
          page,
          limit,
          ...filters,
        },
      });
      // Backend returns: { page, limit, total, totalPages, data: [...] }
      return res.data;
    } catch (error) {
      console.error("Error fetching email logs:", error);
      toast.error("Failed to fetch email logs.");
      return {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        data: [],
      };
    }
  };

  const getEmailAnalytics = async (startDate = null, endDate = null, limit = 50) => {
    try {
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const res = await axios.get("/email/analytics", { params });
      // Backend returns: { data: [...], count: number }
      return res.data || { data: [], count: 0 };
    } catch (error) {
      console.error("Error fetching email analytics:", error);
      toast.error("Failed to fetch email analytics.");
      return { data: [], count: 0 };
    }
  };

  // ===================== ADMIN MANAGEMENT METHODS =====================
  const getAllAdmins = async (page = 1, limit = 50, search = "") => {
    try {
      const res = await axios.get("/admin/all", {
        params: { page, limit, search },
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to fetch admins.");
      return {
        admins: [],
        pagination: { total: 0, limit, offset: 0, hasMore: false },
      };
    }
  };

  const deleteAdmin = async (adminId) => {
    try {
      const res = await axios.delete(`/admin/${adminId}`);
      toast.success(res.data?.message || "Admin deleted successfully.");
      return res.data;
    } catch (error) {
      console.error("Error deleting admin:", error);
      const message = error?.response?.data?.message || "Failed to delete admin.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const initiateAdminPasswordReset = async (email) => {
    try {
      const res = await axios.post("/admin/initiate-password-reset", { email });
      toast.success(res.data?.message || "Password reset initiated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error initiating admin password reset:", error);
      const message = error?.response?.data?.message || "Failed to initiate password reset.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const resetAdminPassword = async (email, otp, password) => {
    try {
      const res = await axios.post("/admin/reset-password", { email, otp, password });
      toast.success(res.data?.message || "Password reset successfully.");
      return res.data;
    } catch (error) {
      console.error("Error resetting admin password:", error);
      const message = error?.response?.data?.message || "Failed to reset password.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getAdminLoginHistory = async (limit = 50, offset = 0) => {
    try {
      const res = await axios.get("/admin/login-history", {
        params: { limit, offset },
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching admin login history:", error);
      toast.error("Failed to fetch login history.");
      return {
        history: [],
        pagination: { total: 0, limit, offset, hasMore: false },
      };
    }
  };

  const getAllAdminsLoginHistory = async (limit = 50, offset = 0, email = null) => {
    try {
      const res = await axios.get("/admin/all-login-history", {
        params: { limit, offset, email },
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching all admins login history:", error);
      toast.error("Failed to fetch login history.");
      return {
        history: [],
        pagination: { total: 0, limit, offset, hasMore: false },
      };
    }
  };

  const verifyPassword = async (password) => {
    try {
      const res = await axios.post("/admin/verify-password", { password });
      return res.data?.success || false;
    } catch (error) {
      console.error("Error verifying password:", error);
      const message = error?.response?.data?.message || "Password verification failed.";
      throw new Error(message);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await axios.post("/admin/change-password", {
        currentPassword,
        newPassword,
      });
      toast.success(res.data?.message || "Password changed successfully.");
      return res.data;
    } catch (error) {
      console.error("Error changing password:", error);
      // If there are detailed validation errors, don't show generic message
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Errors will be shown in the component
        throw error;
      }
      const message = error?.response?.data?.message || "Failed to change password.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== USER MANAGEMENT METHODS =====================
  const createUser = async (userData) => {
    try {
      const res = await axios.post("/users", userData);
      toast.success(res.data?.message || "User created successfully.");
      return res.data;
    } catch (error) {
      console.error("Error creating user:", error);
      const message = error?.response?.data?.message || "Failed to create user.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const res = await axios.put(`/users/${userId}`, userData);
      toast.success(res.data?.message || "User updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating user:", error);
      const message = error?.response?.data?.message || "Failed to update user.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const hardDeleteUser = async (userId) => {
    try {
      const res = await axios.delete(`/users/${userId}`);
      toast.success(res.data?.message || "User permanently deleted.");
      return res.data;
    } catch (error) {
      console.error("Error permanently deleting user:", error);
      const message = error?.response?.data?.message || "Failed to delete user.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const resetUserPassword = async (userId) => {
    try {
      const res = await axios.put(`/users/${userId}/reset-password`);
      toast.success(res.data?.message || "Password reset successfully. Email sent to user.");
      return res.data;
    } catch (error) {
      console.error("Error resetting user password:", error);
      const message = error?.response?.data?.message || "Failed to reset user password.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const importUsers = async (users) => {
    try {
      const res = await axios.post("/users/import", { users });
      const { results } = res.data;
      
      if (results.successful.length > 0 && results.failed.length === 0) {
        toast.success(`Successfully imported ${results.successful.length} user(s). Emails sent.`);
      } else if (results.successful.length > 0 && results.failed.length > 0) {
        toast.warning(`Imported ${results.successful.length} user(s). ${results.failed.length} failed.`);
      } else if (results.failed.length > 0) {
        toast.error(`Import failed for all ${results.failed.length} user(s).`);
      }
      
      return res.data;
    } catch (error) {
      console.error("Error importing users:", error);
      const message = error?.response?.data?.message || "Failed to import users.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const assignTicketToAdmin = async (ticketId, adminId) => {
    try {
      const res = await axios.post(`/admin/tickets/${ticketId}/assign`, {
        assigned_to: adminId,
      });
      toast.success(res.data?.message || "Ticket assigned successfully.");
      return res.data;
    } catch (error) {
      console.error("Error assigning ticket:", error);
      const message = error?.response?.data?.message || "Failed to assign ticket.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== ORDER MANAGEMENT =====================
  const getOrderById = async (orderId) => {
    try {
      // Use admin-specific endpoint for order details
      const res = await axios.get(`/orders/admin/${orderId}`);
      return res.data?.data || res.data;
    } catch (error) {
      console.error("Error fetching order:", error);
      const message = error?.response?.data?.message || "Failed to fetch order details.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const addShipment = async (orderId, shipmentData) => {
    try {
      const res = await axios.post(`/orders/add-shipment/${orderId}`, shipmentData);
      toast.success(res.data?.message || "Shipment added successfully.");
      return res.data;
    } catch (error) {
      console.error("Error adding shipment:", error);
      const message = error?.response?.data?.message || "Failed to add shipment.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== RETURN REQUESTS MANAGEMENT =====================
  const getAllReturnRequests = async (filters = {}) => {
    try {
      const res = await axios.get("/orders/admin/returns", { params: filters });
      // Backend returns: { success: true, data: { count, returnRequests: [...] } }
      return res.data?.data || { count: 0, returnRequests: [] };
    } catch (error) {
      console.error("Error fetching return requests:", error);
      // Don't show toast for expected empty results or data issues
      // Only show for actual errors
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message || "Failed to fetch return requests.";
      
      // If it's a 404 or data issue, return empty array instead of throwing
      if (status === 404 || message.includes("not found") || message.includes("Order not found")) {
        console.warn("Return requests endpoint returned error, returning empty array:", message);
        return { count: 0, returnRequests: [] };
      }
      
      // For other errors, show toast and throw
      toast.error(message);
      throw new Error(message);
    }
  };

  const getReturnRequestById = async (returnId) => {
    try {
      const res = await axios.get(`/orders/admin/returns/${returnId}`);
      // Backend returns: { success: true, data: { returnRequest: {...}, order: {...}, user: {...}, items: [...] } }
      return res.data?.data || res.data;
    } catch (error) {
      console.error("Error fetching return request:", error);
      const message = error?.response?.data?.message || "Failed to fetch return request details.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const approveReturnRequest = async (returnId, adminNote = null) => {
    try {
      const res = await axios.put(`/orders/admin/returns/${returnId}/approve`, { adminNote });
      toast.success(res.data?.message || "Return request approved successfully.");
      return res.data;
    } catch (error) {
      console.error("Error approving return request:", error);
      const message = error?.response?.data?.message || "Failed to approve return request.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const rejectReturnRequest = async (returnId, rejectionReason, adminNote = null) => {
    try {
      const res = await axios.put(`/orders/admin/returns/${returnId}/reject`, {
        rejectionReason,
        adminNote,
      });
      toast.success(res.data?.message || "Return request rejected successfully.");
      return res.data;
    } catch (error) {
      console.error("Error rejecting return request:", error);
      const message = error?.response?.data?.message || "Failed to reject return request.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const processReturnRequest = async (returnId, action, adminNote = null, restoreStock = true) => {
    try {
      const res = await axios.put(`/orders/admin/returns/${returnId}/process`, {
        action, // "processing" or "completed"
        adminNote,
        restoreStock,
      });
      toast.success(res.data?.message || "Return request processed successfully.");
      return res.data;
    } catch (error) {
      console.error("Error processing return request:", error);
      const message = error?.response?.data?.message || "Failed to process return request.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const updateReturnStatus = async (returnId, status, adminNote = null) => {
    try {
      const res = await axios.put(`/orders/admin/returns/${returnId}/status`, {
        status,
        adminNote,
      });
      toast.success(res.data?.message || "Return request status updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating return status:", error);
      const message = error?.response?.data?.message || "Failed to update return request status.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== ABOUT US =====================
  const getAboutUs = async () => {
    try {
      const res = await axios.get("/about");
      return res.data.aboutUs;
    } catch (error) {
      console.error("Error fetching about us:", error);
      toast.error("Failed to fetch about us content.");
      throw new Error(error?.response?.data?.message || "Failed to fetch about us content.");
    }
  };

  const updateAboutUs = async (aboutUsData) => {
    try {
      const res = await axios.put("/about", aboutUsData);
      toast.success(res.data?.message || "About us content updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating about us:", error);
      const message = error?.response?.data?.message || "Failed to update about us content.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== REVIEWS =====================
  const getReviews = async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = { page, limit, ...filters };
      const res = await axios.get("/reviews", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to fetch reviews.");
      throw new Error(error?.response?.data?.message || "Failed to fetch reviews.");
    }
  };

  const getAllReviews = async (page = 1, limit = 20, filters = {}) => {
    try {
      const params = { page, limit, ...filters };
      const res = await axios.get("/reviews/admin/all", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching all reviews:", error);
      toast.error("Failed to fetch reviews.");
      throw new Error(error?.response?.data?.message || "Failed to fetch reviews.");
    }
  };

  const createReview = async (reviewData) => {
    try {
      // Use admin endpoint if available, otherwise fall back to public endpoint
      const endpoint = "/reviews/admin";
      const res = await axios.post(endpoint, reviewData);
      toast.success(res.data?.message || "Review created successfully.");
      return res.data;
    } catch (error) {
      console.error("Error creating review:", error);
      const message = error?.response?.data?.message || "Failed to create review.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const updateReview = async (reviewId, reviewData) => {
    try {
      const res = await axios.put(`/reviews/admin/${reviewId}`, reviewData);
      toast.success(res.data?.message || "Review updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating review:", error);
      const message = error?.response?.data?.message || "Failed to update review.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      const res = await axios.delete(`/reviews/admin/${reviewId}`);
      toast.success(res.data?.message || "Review deleted successfully.");
      return res.data;
    } catch (error) {
      console.error("Error deleting review:", error);
      const message = error?.response?.data?.message || "Failed to delete review.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const toggleReviewApproval = async (reviewId, isApproved) => {
    try {
      const res = await axios.patch(`/reviews/admin/${reviewId}/approval`, { is_approved: isApproved });
      toast.success(res.data?.message || `Review ${isApproved ? "approved" : "unapproved"} successfully.`);
      return res.data;
    } catch (error) {
      console.error("Error toggling review approval:", error);
      const message = error?.response?.data?.message || "Failed to update review approval.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== LEGAL PAGES =====================
  const getLegalPage = async (pageType) => {
    try {
      const res = await axios.get(`/legal/${pageType}`);
      return res.data.page;
    } catch (error) {
      console.error("Error fetching legal page:", error);
      toast.error("Failed to fetch legal page content.");
      throw new Error(error?.response?.data?.message || "Failed to fetch legal page content.");
    }
  };

  const updateLegalPage = async (pageType, pageData) => {
    try {
      const res = await axios.put(`/legal/${pageType}`, pageData);
      toast.success(res.data?.message || "Legal page updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating legal page:", error);
      const message = error?.response?.data?.message || "Failed to update legal page content.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== FAQ =====================
  const getFAQs = async (filters = {}) => {
    try {
      const params = { ...filters };
      const res = await axios.get("/faq", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to fetch FAQs.");
      throw new Error(error?.response?.data?.message || "Failed to fetch FAQs.");
    }
  };

  const getAllFAQs = async (filters = {}) => {
    try {
      const params = { ...filters };
      const res = await axios.get("/faq/admin/all", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching all FAQs:", error);
      toast.error("Failed to fetch FAQs.");
      throw new Error(error?.response?.data?.message || "Failed to fetch FAQs.");
    }
  };

  const createFAQ = async (faqData) => {
    try {
      const res = await axios.post("/faq/admin", faqData);
      toast.success(res.data?.message || "FAQ created successfully.");
      return res.data;
    } catch (error) {
      console.error("Error creating FAQ:", error);
      const message = error?.response?.data?.message || "Failed to create FAQ.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const updateFAQ = async (faqId, faqData) => {
    try {
      const res = await axios.put(`/faq/admin/${faqId}`, faqData);
      toast.success(res.data?.message || "FAQ updated successfully.");
      return res.data;
    } catch (error) {
      console.error("Error updating FAQ:", error);
      const message = error?.response?.data?.message || "Failed to update FAQ.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const deleteFAQ = async (faqId) => {
    try {
      const res = await axios.delete(`/faq/admin/${faqId}`);
      toast.success(res.data?.message || "FAQ deleted successfully.");
      return res.data;
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      const message = error?.response?.data?.message || "Failed to delete FAQ.";
      toast.error(message);
      throw new Error(message);
    }
  };

  // ===================== AUDIT LOGS =====================
  const getAuditLogs = async (filters = {}) => {
    try {
      const res = await axios.get("/admin/audit-logs", { params: filters });
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to fetch audit logs.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getAuditStats = async (filters = {}) => {
    try {
      const res = await axios.get("/admin/audit-logs/stats", { params: filters });
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to fetch audit statistics.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getAuditLogById = async (id) => {
    try {
      const res = await axios.get(`/admin/audit-logs/${id}`);
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to fetch audit log.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const exportAuditLogsCSV = async (filters = {}) => {
    try {
      const res = await axios.get("/admin/audit-logs/export/csv", {
        params: filters,
        responseType: "blob",
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit-logs-${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Audit logs exported successfully!");
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to export audit logs.";
      toast.error(message);
      throw new Error(message);
    }
  };

  const getAuditFilters = async () => {
    try {
      const res = await axios.get("/admin/audit-logs/filters");
      return res.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to fetch filter options.";
      toast.error(message);
      throw new Error(message);
    }
  };

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        login,
        logout,
        loading,
        fetchAdmin,
        registerAdmin,
        verifyOTP,
        resendOTP,
        getAllUsers,
        uploadFiles,
        uploadFileFromUrl,
        uploadAdminAvatar,
        getFiles,
        deleteFiles,
        createCoupon,
        updateCoupon,
        deleteCoupon,
        listCoupons,
        validateCoupon,
        applyCoupon,
        getCouponInfo,
        getMonthlyStats,
        getRecentOrders,
        getAllTickets,
        getTicketWithReplies,
        replyToTicket,
        changeTicketStatus,
        getAllSystemSettings,
        getSystemSetting,
        updateSystemSetting,
        getPaymentGatewayStatus,
        enablePaymentGateway,
        disablePaymentGateway,
        getTaxRate,
        updateTaxRate,
        getSiteInfo,
        updateSiteInfo,
        enableDbBackup,
        disableDbBackup,
        enableSentry,
        disableSentry,
        sendBulkEmail,
        sendPreviewEmail,
        getEmailLogs,
        getEmailAnalytics,
        getAllAdmins,
        deleteAdmin,
        getAdminLoginHistory,
        getAllAdminsLoginHistory,
        verifyPassword,
        changePassword,
        createUser,
        updateUser,
        hardDeleteUser,
        resetUserPassword,
        importUsers,
        assignTicketToAdmin,
        getOrderById,
        addShipment,
        getAllReturnRequests,
        getReturnRequestById,
        approveReturnRequest,
        rejectReturnRequest,
        processReturnRequest,
        updateReturnStatus,
        getAboutUs,
        updateAboutUs,
        getReviews,
        getAllReviews,
        createReview,
        updateReview,
        deleteReview,
        toggleReviewApproval,
        getLegalPage,
        updateLegalPage,
        getFAQs,
        getAllFAQs,
        createFAQ,
        updateFAQ,
        deleteFAQ,
        getAuditLogs,
        getAuditStats,
        getAuditLogById,
        exportAuditLogsCSV,
        getAuditFilters,
        initiateAdminPasswordReset,
        resetAdminPassword,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);
